import * as React from 'react';
import { Link } from 'react-router-dom';
import { certificatePages, offeringCards } from '../data/websiteContent';
import bifmaIcon from '../assets/service-icons/bifma.svg';
import ceIcon from '../assets/service-icons/ce.svg';
import complianceIcon from '../assets/service-icons/compliance.svg';
import gmpIcon from '../assets/service-icons/gmp.svg';
import isoIcon from '../assets/service-icons/iso.svg';
import organicIcon from '../assets/service-icons/organic.svg';
import rohsIcon from '../assets/service-icons/rohs.svg';
import '../styles/MarketingPages.css';

const serviceDescriptions: Record<string, string> = {
  'ISO 9001:2015':
    'It is a globally accepted and highly compatible standard that assures consistent quality and customer confidence.',
  'ISO 13485:2016':
    'Medical device quality management certification that supports regulatory compliance and stronger product safety.',
  'ISO 8000:2014':
    'Data quality standard to improve trusted records, measurable accuracy, and better enterprise reporting.',
  'GMP Compliance':
    "India's leading GMP certification support to help your business become GMP certified with confidence.",
  'ISO 45001:2018':
    'Occupational health and safety framework to reduce workplace risks and improve employee wellbeing.',
  'ISO 50000:2011':
    'Energy management standard that helps organizations reduce consumption, control costs, and improve efficiency.',
  'CE Marking':
    'Conformity pathway that supports market readiness and compliance with applicable product directives.',
  'ISO 14001:2015':
    'Environmental management system that strengthens sustainability commitments and compliance goals.',
  'Kosher Compliance':
    'Certification pathway for food and product lines that require validated kosher process controls.',
  'BIFMA Compliance':
    'BIFMA level support for multi-attribute property standards and third-party certification assurance.',
  HACCP:
    'Food safety management approach based on critical control points to prevent and reduce production risks.',
  'FDA Compliance':
    'Guidance for regulated products that require process control, quality evidence, and FDA-aligned documentation.',
  'Organic Compliance':
    'Organic certification support for product validation and transparent label trust in domestic and export markets.',
  'ISO 29990:2010':
    'Learning services management standard focused on service quality, outcomes, and continual improvement.',
  'ISO 22716:2007':
    'Good manufacturing practices for cosmetics that help ensure quality, hygiene, and process consistency.',
  'RoHS Compliance':
    "Product-level compliance support aligned with the EU's RoHS directives for restricted substances.",
  'ISO 22000:2018':
    'Food safety management certification for end-to-end control across the supply chain.',
  'ISO 15189:2012':
    'Laboratory quality and competence standard designed for reliable test processes and accurate outcomes.',
  'ISO 27001:2013':
    'Information security management framework for risk controls, data protection, and audit readiness.',
  'ISO 20000-1:2018':
    'IT service management standard that improves service delivery, governance, and customer value.',
  'ISO 15378:2017':
    'Primary packaging material quality standard for medicinal products with GMP-integrated requirements.',
};

const referenceServiceOrder = [
  'ISO 9001:2015',
  'GMP Compliance',
  'CE Marking',
  'BIFMA Compliance',
  'Organic Compliance',
  'RoHS Compliance',
  'ISO 27001:2013',
  'ISO 13485:2016',
  'ISO 45001:2018',
  ...offeringCards.filter(
    (item) =>
      ![
        'ISO 9001:2015',
        'GMP Compliance',
        'CE Marking',
        'BIFMA Compliance',
        'Organic Compliance',
        'RoHS Compliance',
        'ISO 27001:2013',
        'ISO 13485:2016',
        'ISO 45001:2018',
      ].includes(item)
  ),
];

const getServiceIcon = (title: string) => {
  if (title.includes('ISO')) {
    return isoIcon;
  }

  if (title.includes('CE')) {
    return ceIcon;
  }

  if (title.includes('RoHS')) {
    return rohsIcon;
  }

  if (title.includes('BIFMA')) {
    return bifmaIcon;
  }

  if (title.includes('GMP')) {
    return gmpIcon;
  }

  if (title.includes('Organic')) {
    return organicIcon;
  }

  return complianceIcon;
};

const displayServiceTitle = (title: string) => title.replace(' Compliance', '');

type ServicesShowcaseSectionProps = {
  id?: string;
};

export const ServicesShowcaseSection: React.FC<ServicesShowcaseSectionProps> = ({ id }) => {
  const [activeService, setActiveService] = React.useState(referenceServiceOrder[0]);
  const detailRef = React.useRef<HTMLDivElement>(null);
  const activePage =
    certificatePages.find((page) => page.title === activeService) ?? certificatePages[0];

  const handleServiceClick = (service: string) => {
    setActiveService(service);

    window.requestAnimationFrame(() => {
      detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <section id={id} className="marketing-page services-page-reference services-page-modern services-page-visual">
      <section className="services-shell">
        <header className="services-heading-reference services-heading-modern services-heading-showcase">
          <span className="eyebrow-pill">Our service</span>
          <h1>Services We Provide</h1>
        </header>

        <section className="services-showcase-grid" role="tablist" aria-label="Certification services list">
          {referenceServiceOrder.map((item, index) => {
            const isActive = item === activeService;

            return (
              <button
                key={`${item}-${index}`}
                type="button"
                role="tab"
                className={`services-showcase-card services-showcase-tab ${isActive ? 'is-highlighted is-active' : ''}`}
                onClick={() => handleServiceClick(item)}
                aria-controls="service-detail-panel"
                aria-selected={isActive}
              >
                <span className="service-reference-icon services-showcase-icon">
                  <img src={getServiceIcon(item)} alt={`${item} icon`} className="service-reference-icon-image" />
                </span>

                <div className="service-reference-copy services-showcase-copy">
                  <span className="offering-name services-showcase-title">{displayServiceTitle(item)}</span>
                  <p>
                    {serviceDescriptions[item]
                      ?? 'Trusted certification support for compliance and business credibility.'}
                  </p>
                </div>
              </button>
            );
          })}
        </section>

        <section
          id="service-detail-panel"
          ref={detailRef}
          className="service-detail-panel detail-layout"
          aria-live="polite"
        >
          <div className="detail-visual" style={{ '--accent': activePage.accent } as React.CSSProperties}>
            <span className="eyebrow-pill">Selected service</span>
            <h1>{activePage.title}</h1>
            <p>{activePage.subtitle}</p>
            <div className="detail-badge">{activePage.slug}.html</div>
            <div className="detail-metrics">
              <div>
                <strong>Scope</strong>
                <span>Defined activities</span>
              </div>
              <div>
                <strong>Status</strong>
                <span>Verification ready</span>
              </div>
            </div>
          </div>

          <div className="content-card">
            <h2>Overview</h2>
            <p>{activePage.summary}</p>
            <h3>Highlights</h3>
            <ul>
              {activePage.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
            <div className="info-box detail-notes">
              <h3>Best for</h3>
              <p>Organizations that want a clear certificate presentation and a public verification journey.</p>
            </div>
            <div className="page-actions">
              <Link to={`/${activePage.slug}.html`} className="btn btn-secondary">
                Open Service Page
              </Link>
              <Link to="/contact" className="btn btn-primary">
                Request Information
              </Link>
            </div>
          </div>
        </section>
      </section>
    </section>
  );
};

const OfferingCertificatesPage: React.FC = () => <ServicesShowcaseSection />;

export default OfferingCertificatesPage;
